import type { ReactNode, RefObject } from 'react'
import { forwardRef, useId } from 'react'
import { Trans } from '@lingui/react/macro'
import type { SwitchProps } from '@radix-ui/react-switch'
import * as Switch from '@radix-ui/react-switch'

import { useDelay } from '@jotta/hooks/useDelay'
import Spinner from '@jotta/ui/Spinner'
import { cn } from '@jotta/utils/css'

function LabelWrapper({
  htmlFor,
  label,
  labelId,
  children,
  className,
  labelPosition,
}: {
  htmlFor: string
  label?: string
  labelId: string
  children: ReactNode
  className?: string
  labelPosition: 'left' | 'right'
}) {
  if (!label) {
    return <>{children}</>
  }

  return (
    <div className={cn('flex items-center gap-2', className)}>
      {labelPosition === 'right' && children}
      <label id={labelId} htmlFor={htmlFor} className="cursor-pointer">
        {label}
      </label>
      {labelPosition === 'left' && children}
    </div>
  )
}

export interface ToggleButtonProps extends SwitchProps {
  loading?: boolean
  disabled?: boolean
  className?: string
  label?: string
  labelPosition?: 'left' | 'right'
  ref?: RefObject<HTMLButtonElement | null>
}

export function ToggleButton({
  checked,
  defaultChecked,
  loading,
  disabled,
  label,
  className,
  children,
  labelPosition = 'left',
  ref,
  ...props
}: ToggleButtonProps) {
  const switchId = useId()
  const labelId = useId()

  const showSpinner = useDelay({
    value: loading,
    initialValue: false,
    delayMs: 500,
  })

  if (showSpinner) {
    return (
      <LabelWrapper
        htmlFor={switchId}
        labelId={labelId}
        label={label}
        className={className}
        labelPosition={labelPosition}
      >
        <Spinner size="medium" aria-hidden="true" className="w-14" />
        <span className="sr-only">
          <Trans>Updating value</Trans>
        </span>
      </LabelWrapper>
    )
  }

  return (
    <LabelWrapper
      htmlFor={switchId}
      labelId={labelId}
      label={label}
      className={className}
      labelPosition={labelPosition}
    >
      <Switch.Root
        id={switchId}
        ref={ref}
        checked={checked}
        defaultChecked={defaultChecked}
        className={cn(
          'group relative h-8 w-14 shrink-0 rounded-full px-0.5 transition-colors duration-300',
          'outline-none focus-visible:shadow-none focus-visible:outline-0 focus-visible:ring-2 focus-visible:ring-btn-primary focus-visible:ring-offset-2 focus-visible:transition-all focus-visible:duration-200',
          'data-[state=unchecked]:border-2 data-[state=unchecked]:border-outline-variant data-[state=unchecked]:bg-surface-container-highest',
          'data-[state=checked]:bg-m3-primary data-[state=checked]:px-1',
          {
            'opacity-25': disabled,
          },
        )}
        disabled={disabled}
        aria-labelledby={label ? labelId : undefined}
        {...props}
      >
        <div
          className={cn(
            'absolute h-4 w-4 rounded-full bg-on-surface/10 ring-on-surface/10 group-hover:ring-[0.75rem]',
            {
              'left-1.5 top-1.5': !checked,
              'right-2 top-2': checked,
            },
          )}
        />
        <Switch.Thumb
          className={cn(
            'block h-6 w-6 rounded-full bg-outline ring-on-surface/10 transition duration-300 ease-in-out group-active:scale-[115%]',
            'data-[state=unchecked]:scale-[0.66] data-[state=unchecked]:p-1',
            'data-[state=checked]:translate-x-full data-[state=checked]:scale-100 data-[state=checked]:bg-on-primary',
          )}
        />
      </Switch.Root>
      {children}
    </LabelWrapper>
  )
}
